"use client";
import React, { useEffect, useState, useRef } from "react";
import styles from "@components/CustomComponents/LocatorStore/Map/index.module.scss";
import { getLocalizedValue } from "@/utils/helper";
import { useRouter } from "next/router";

// Parse default location properties safely
const DEFAULT_LOCATION = process.env.NEXT_PUBLIC_DEFAULT_MAP_LOCATION
  ? process.env.NEXT_PUBLIC_DEFAULT_MAP_LOCATION.split(",").map(Number)
  : [13.7563, 100.5018]; // Default fallback to Bangkok

const MapView = props => {
  const {
    location = {},
    locationResult = [],
    showPopup = false,
    markerData = [],
  } = props;

  const { locale } = useRouter();

  // Standardize language locale tags to valid Bing culture/market pairs
  const getBingCulture = lang => {
    if (!lang) return "en-US";
    if (lang === "th") return "th-TH";
    if (lang === "zh") return "zh-CN";
    return lang.includes("-") ? lang : `${lang}-${lang.toUpperCase()}`;
  };

  const mapCultureLocale = getBingCulture(locale);
  const scriptLangParam = locale === "th" ? "th" : locale;

  const [mapReady, setMapReady] = useState(false);
  const [containerMounted, setContainerMounted] = useState(false);

  const mapContainerRef = useRef(null);
  const bingMapInstance = useRef(null);
  const currentInfoboxRef = useRef(null);

  // Guarantee the target container is fully painted in the DOM to avoid initial loading crashes
  useEffect(() => {
    if (mapContainerRef.current) {
      setContainerMounted(true);
    }
  }, []);

  // 1. CLEAN SCRIPT INJECTION ENGINE
  useEffect(() => {
    if (typeof window === "undefined") return;

    if (!window.Microsoft) window.Microsoft = {};
    if (!window.Microsoft.Maps) window.Microsoft.Maps = {};
    if (!window.Microsoft.Maps.NetworkCallbacks)
      window.Microsoft.Maps.NetworkCallbacks = {};
    window.Microsoft.Maps.NetworkCallbacks.f_logCallbackRequest = () => {};

    const scriptId = "bing-maps-core-script";
    let script = document.getElementById(scriptId);

    window.initNativeBingMap = () => {
      setMapReady(true);
    };

    if (!script) {
      const legacyScripts = document.querySelectorAll(
        'script[src*="://bing.com"]'
      );
      legacyScripts.forEach(s => s.remove());

      if (window.Microsoft && window.Microsoft.Maps) {
        delete window.Microsoft.Maps;
      }

      script = document.createElement("script");
      script.id = scriptId;
      script.type = "text/javascript";
      script.async = true;
      script.defer = true;
      script.src = `https://www.bing.com/api/maps/mapcontrol?setLang=${scriptLangParam}&callback=initNativeBingMap&key=${process.env.NEXT_PUBLIC_MAP_KEY}`;

      document.head.appendChild(script);
    } else {
      if (
        window.Microsoft &&
        window.Microsoft.Maps &&
        window.Microsoft.Maps.Map
      ) {
        setMapReady(true);
      }
    }
  }, [mapCultureLocale, scriptLangParam]);

  // 2. ONE-TIME NATIVE BASE MAP INITIALIZATION
  useEffect(() => {
    if (
      !mapReady ||
      !containerMounted ||
      !mapContainerRef.current ||
      !window.Microsoft?.Maps?.Map
    )
      return;

    if (bingMapInstance.current) {
      bingMapInstance.current.dispose();
    }

    const initialLat = location?.latitude
      ? Number(location.latitude)
      : DEFAULT_LOCATION[0];
    const initialLng = location?.longitude
      ? Number(location.longitude)
      : DEFAULT_LOCATION[1];
    const initialLocation = new window.Microsoft.Maps.Location(
      initialLat,
      initialLng
    );

    const map = new window.Microsoft.Maps.Map(mapContainerRef.current, {
      center: initialLocation,
      zoom: 10,
      disableStreetside: true,
      navigationBarMode: window.Microsoft.Maps.NavigationBarMode.square,
      showMapTypeSelector: true,
      disableScrollWheelZoom: true,
      culture: mapCultureLocale,
      setLang: scriptLangParam,
    });

    bingMapInstance.current = map;

    return () => {
      if (bingMapInstance.current) {
        bingMapInstance.current.dispose();
        bingMapInstance.current = null;
      }
    };
  }, [mapReady, containerMounted, mapCultureLocale, scriptLangParam]);

  // 3. FLICKER-FREE LIVE UPDATES
  useEffect(() => {
    const map = bingMapInstance.current;
    if (!map || !mapReady || !window.Microsoft?.Maps) return;

    // Reset current graphics layers
    map.entities.clear();
    if (currentInfoboxRef.current) {
      currentInfoboxRef.current.setMap(null);
      currentInfoboxRef.current = null;
    }

    const hasValidLocation =
      location && location?.latitude && location?.longitude;
    const hasValidMarker =
      markerData && markerData?.Latitude && markerData?.Longitude;

    let targetLat = DEFAULT_LOCATION[0];
    let targetLng = DEFAULT_LOCATION[1];

    if (hasValidMarker) {
      targetLat = Number(markerData.Latitude);
      targetLng = Number(markerData.Longitude);
    } else if (hasValidLocation) {
      targetLat = Number(location.latitude);
      targetLng = Number(location.longitude);
    }

    const targetLocation = new window.Microsoft.Maps.Location(
      targetLat,
      targetLng
    );

    map.setView({
      center: targetLocation,
      zoom: 13,
      animate: true,
    });

    // Populate pins dynamically from results array
    locationResult.forEach(locs => {
      if (locs.lat && locs.lng) {
        const pinLoc = new window.Microsoft.Maps.Location(
          Number(locs.lat),
          Number(locs.lng)
        );
        const pin = new window.Microsoft.Maps.Pushpin(pinLoc);
        map.entities.push(pin);
      }
    });

    // Render localized InfoBox markup layer
    if (showPopup && hasValidMarker) {
      const infoLoc = new window.Microsoft.Maps.Location(
        Number(markerData.Latitude),
        Number(markerData.Longitude)
      );

      const getMarkerColor = classification => {
        switch (classification?.toLowerCase()) {
          case "kec":
            return "#84A648";
          case "distributor":
            return "#00a7ce";
          case "dealer":
            return "#9d9d9d";
          default:
            return "#000000";
        }
      };

      const infoboxHtml = `
        <div class="customInfobox" style="
          font-family: sans-serif; 
          width: 280px; 
          max-width: 280px; 
          background: #ffffff; 
          overflow: hidden; 
          box-sizing: border-box;
          padding: 14px;
          box-shadow: 0px 4px 15px rgba(0,0,0,0.15);
          border-radius: 6px;
          position: relative;
        ">
          <button id="native-infobox-close-trigger" style="
            position: absolute;
            top: 10px;
            right: 10px;
            background: none;
            border: none;
            font-size: 16px;
            font-weight: bold;
            color: #666;
            cursor: pointer;
            padding: 0;
            line-height: 1;
            width: 20px;
            height: 20px;
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: 50%;
          ">
            ✕
          </button>

          <h3 style="display: flex; margin: 0; padding-bottom: 6px; align-items: flex-start; padding-right: 20px;">
            <span style="
              background: ${getMarkerColor(markerData?.ContactClassification)}; 
              margin-right: 8px; 
              color: white; 
              font-weight: bold; 
              width: 22px; 
              height: 22px; 
              min-width: 22px;
              display: flex; 
              align-items: center; 
              justify-content: center;
              font-size: 12px;
            ">
              ${markerData?.id || ""}
            </span>
            <span style="
              font-weight: bold; 
              font-size: 14px; 
              color: #000; 
              word-break: break-word;
              line-height: 1.3;
            ">${getLocalizedValue(markerData, "LocationName", locale)}</span>
          </h3>
          <p style="
            margin: 4px 0 0 30px; 
            color: #333; 
            word-break: break-word;                   
            white-space: normal;
            font-size: 12px; 
            font-weight: normal;
            line-height: 1.4;
          ">
            ${getLocalizedValue(markerData, "AddressLine", locale) || ""} 
            ${getLocalizedValue(markerData, "Locality", locale) || ""} 
            <span style="display: block; margin-top: 4px; font-weight: 500;">${markerData?.Phone || ""}</span>
            ${
              markerData?.WebURL
                ? `
              <span style="margin-top: 6px; display: block;">
                <a href="${markerData.WebURL}" target="_blank" rel="noopener noreferrer" style="color: #2563eb; text-decoration: underline; font-weight: 500;">
                  Visit Website
                </a>
              </span>`
                : ""
            }
            <span style="display: block; margin-top: 4px; color: #666;">${markerData?.OpeningHours || ""}</span>
          </p>
        </div>`;

      const infobox = new window.Microsoft.Maps.Infobox(infoLoc, {
        htmlContent: infoboxHtml,
        visible: true,
        actions: [],
      });

      infobox.setMap(map);
      currentInfoboxRef.current = infobox;

      // Event listener assignment
      setTimeout(() => {
        const closeBtn = document.getElementById(
          "native-infobox-close-trigger"
        );
        if (closeBtn) {
          closeBtn.addEventListener("click", () => {
            if (currentInfoboxRef.current) {
              currentInfoboxRef.current.setMap(null);
              currentInfoboxRef.current = null;
            }
          });
        }
      }, 50);
    }
  }, [mapReady, location, markerData, locationResult, showPopup, locale]);

  return (
    <div className={styles.mapWrapper}>
      <div
        ref={mapContainerRef}
        className={styles["react-bingmaps"]}
        style={{
          width: "100%",
          height: "100%",
          minHeight: "450px",
          display: "block",
        }}
      />
    </div>
  );
};

export default MapView;
